import React, { useState } from 'react';
import { Icon } from '../design/icons';
import { accountMaskSuffix } from '../design/dataAdapters';

// Account picker for a freshly linked bank connection. Plaid reports an
// account type for most accounts (so `typeEditable` is false and the type is
// locked); when it can't, the account arrives with a server-side GUESS
// (`typeEditable: true`) and the row shows a Bank/Card toggle — the type
// drives repayment classification and balance signs, so the user must be
// able to correct it before anything is saved.
export default function AccountPicker({ accounts, onSelect, onClose }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [typeOverrides, setTypeOverrides] = useState({});
  const typeOf = (account) => typeOverrides[account.id] || account.type || 'depository';
  const toggle = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const setType = (id, type) => setTypeOverrides((prev) => ({ ...prev, [id]: type }));
  const selected = accounts
    .filter((a) => selectedIds.has(a.id))
    .map((a) => ({ ...a, type: typeOf(a) }));

  return (
    <>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>
        Transactions from the selected accounts will be imported. Credit cards are tracked separately from cash.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {accounts.map((account) => {
          const isSelected = selectedIds.has(account.id);
          const type = typeOf(account);
          const isCredit = type === 'credit';
          return (
            <div
              key={account.id}
              onClick={() => toggle(account.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(account.id); } }}
              className="pressable"
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                background: isSelected ? 'var(--accent-soft)' : 'var(--surface-2)',
                border: `1px solid ${isSelected ? 'color-mix(in srgb, var(--accent) 45%, var(--line))' : 'var(--line)'}`,
                borderRadius: 8, color: 'var(--ink)', fontSize: 14, cursor: 'pointer', textAlign: 'left', width: '100%', boxSizing: 'border-box',
              }}
            >
              <span className="iconwrap icon-surface" style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }}>
                <Icon name={isCredit ? 'card' : 'bank'} size={18} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="title" style={{ fontWeight: 600 }}>{account.name}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  {account.subtype ? `${account.subtype} · ` : ''}{account.institution?.name || ''}{accountMaskSuffix(account.name, account.last_four, ' ····')}
                </div>
                {account.typeEditable && (
                  <div style={{ display: 'inline-flex', gap: 0, marginTop: 6, border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
                    {[
                      { value: 'depository', label: 'Bank' },
                      { value: 'credit', label: 'Card' },
                    ].map((opt) => (
                      <span
                        key={opt.value}
                        onClick={(e) => { e.stopPropagation(); setType(account.id, opt.value); }}
                        style={{
                          padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                          background: type === opt.value ? 'var(--accent)' : 'transparent',
                          color: type === opt.value ? 'var(--bg)' : 'var(--ink-3)',
                        }}
                      >
                        {opt.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 7, flexShrink: 0, border: `1.5px solid ${isSelected ? 'var(--accent)' : 'var(--line-strong)'}`, background: isSelected ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isSelected && <Icon name="check" size={13} sw={2.5} style={{ color: 'var(--bg)' }} />}
              </span>
            </div>
          );
        })}
      </div>
      <button
        disabled={selected.length === 0}
        onClick={() => onSelect(selected)}
        className="pressable"
        style={{
          marginTop: 12, width: '100%', height: 44, borderRadius: 8,
          background: selected.length ? 'var(--accent)' : 'var(--surface-2)',
          border: `1px solid ${selected.length ? 'var(--accent)' : 'var(--line)'}`,
          color: selected.length ? 'var(--bg)' : 'var(--ink-3)', fontSize: 14, fontWeight: 600,
          cursor: selected.length ? 'pointer' : 'not-allowed',
        }}
      >
        {selected.length === 0 ? 'Select accounts to import' : `Import ${selected.length} account${selected.length > 1 ? 's' : ''}`}
      </button>
      <button onClick={onClose} className="btn-quiet is-block pressable" style={{ marginTop: 8 }}>
        Cancel
      </button>
    </>
  );
}
